import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'antd';
import { BellOutlined, ClockCircleOutlined, CheckOutlined,
    EyeOutlined, FileOutlined, CloseOutlined, VideoCameraOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { useTheme } from '@/context/theme';
import { snoozeEvent, acknowledgeEvent } from '@/services/eventsService';

const SNOOZE_OPTIONS = [
    { label: '5 min', value: 5 },
    { label: '10 min', value: 10 },
    { label: '30 min', value: 30 },
];

const RSVP_OPTIONS = [
    { label: '✅ Accept', value: 'accepted', style: { background: '#03488A', color: '#fff' } },
    { label: '❌ Decline', value: 'declined', danger: true },
    { label: '🤔 Maybe', value: 'maybe', style: { borderColor: '#F59E0B', color: '#F59E0B' } },
];

const REMINDER_SYNC_CHANNEL = 'dymaxtech-reminder-sync';
const REMINDER_SYNC_STORAGE_KEY = 'dymaxtech-reminder-handled';

const GlobalReminderModal = () => {
    const { isDarkMode } = useTheme();
    const [visible, setVisible] = useState(false);
    const [reminder, setReminder] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [snoozing, setSnoozing] = useState(false);
    const [activeSnooze, setActiveSnooze] = useState(null);
    const [activeRespond, setActiveRespond] = useState(null);
    const snoozeTimerRef = useRef(null);
    const audioRef = useRef(null);
    // Deduplication: track last fired event ID + timestamp to prevent 3-socket triple-fire
    const lastEventRef = useRef({ id: null, ts: 0 });
    // Audio unlock: track whether user has interacted with the page
    const audioUnlockedRef = useRef(false);
    const handledRemindersRef = useRef(new Set());
    const broadcastChannelRef = useRef(null);
    const currentAdminIdRef = useRef(null);
    const activeReminderIdRef = useRef(null);
    const currentAdmin = useSelector(selectCurrentAdmin); // Use imported selector

    const isCreator = reminder?.user?.id?.toString() === currentAdmin?._id?.toString();
    const isRsvp = reminder?.rsvpEnabled;

    useEffect(() => {
        currentAdminIdRef.current = currentAdmin?._id?.toString() || null;
    }, [currentAdmin?._id]);

    useEffect(() => {
        activeReminderIdRef.current = getReminderId(reminder);
    }, [reminder]);

    const getReminderId = (value) => (
        value?._id?.toString?.() ||
        value?.reminderId?.toString?.() ||
        value?.relatedData?.reminderId?.toString?.() ||
        ''
    );

    const closeModal = (options = {}) => {
        stopSound();
        setVisible(false);
        setReminder(null);
        if (!options.preserveSnoozeTimer && snoozeTimerRef.current) {
            clearTimeout(snoozeTimerRef.current);
            snoozeTimerRef.current = null;
        }
    };

    const handleReminderHandled = (payload = {}) => {
        const handledId = getReminderId(payload);
        if (!handledId) return;

        handledRemindersRef.current.add(handledId);

        if (activeReminderIdRef.current === handledId) {
            closeModal({ preserveSnoozeTimer: payload.action === 'snoozed' });
        }
    };

    const publishReminderHandled = (payload) => {
        if (!payload?.reminderId) return;
        handledRemindersRef.current.add(payload.reminderId.toString());

        try {
            broadcastChannelRef.current?.postMessage(payload);
        } catch (_) { }

        try {
            localStorage.setItem(REMINDER_SYNC_STORAGE_KEY, JSON.stringify({
                ...payload,
                emittedAt: Date.now(),
            }));
        } catch (_) { }
    };

    const hasCurrentUserHandled = (reminderData) => {
        const currentUserId = currentAdminIdRef.current;
        if (!currentUserId) return false;

        return (reminderData?.acknowledgments || []).some((ack) => {
            const ackUserId = ack.userId?.toString?.() || ack.userId;
            return ackUserId === currentUserId && ['acknowledged', 'dismissed'].includes(ack.status);
        });
    };

    useEffect(() => {
        console.log('[GlobalReminderModal] MOUNTED. User ID:', currentAdmin?._id, 'Permission:', Notification.permission);
        
        audioRef.current = new Audio('/mixkit-happy-bells-notification-937.wav');
        audioRef.current.loop = true;

        // ── Audio unlock on first user interaction ────────────────────────
        // Browsers block autoplay until the user has interacted with the page.
        // We prime the audio on the first click/keydown so it plays reliably
        // when a reminder fires later (even if the tab is idle).
        const unlockAudio = () => {
            if (audioUnlockedRef.current) return;
            audioUnlockedRef.current = true;
            const audio = audioRef.current;
            if (audio) {
                audio.muted = true;
                audio.play().then(() => {
                    audio.pause();
                    audio.currentTime = 0;
                    audio.muted = false;
                }).catch(() => { 
                    audio.muted = false; 
                });
            }
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('keydown', unlockAudio);
        };
        document.addEventListener('click', unlockAudio);
        document.addEventListener('keydown', unlockAudio);

        const handleReminderEvent = async (event) => {
            const data = event.detail;
            console.log('[GlobalReminderModal] reminder_received fired:', data?.notificationType, data?.relatedData);

            // Show modal for reminder type AND any notification that carries a reminderId
            const isReminderType = data.type === 'reminder' || data.notificationType === 'reminder';
            const hasReminderId = !!data.relatedData?.reminderId;
            console.log('[GlobalReminderModal] isReminderType:', isReminderType, '| hasReminderId:', hasReminderId);
            if (!isReminderType && !hasReminderId) {
                console.log('[GlobalReminderModal] Filtered out — not a reminder type and no reminderId');
                return;
            }

            // ── Deduplication guard ───────────────────────────────────────
            const eventId = data._id?.toString() || data.relatedData?.reminderId?.toString() || '';
            const now = Date.now();
            if (eventId && eventId === lastEventRef.current.id && now - lastEventRef.current.ts < 1000) {
                console.log('[GlobalReminderModal] Duplicate — skipped');
                return;
            }
            lastEventRef.current = { id: eventId, ts: now };
            console.log('[GlobalReminderModal] Passed dedup — opening modal for:', eventId);

            if (data.relatedData?.reminderId) {
                console.log('[GlobalReminderModal] Fetching full event details for ID:', data.relatedData.reminderId);
                try {
                    const res = await axios.get(`/events/${data.relatedData.reminderId}`);
                    if (res.data?.reminder) {
                        console.log('[GlobalReminderModal] Fetch success. RSVP Enabled:', res.data.reminder.rsvpEnabled);
                        if (handledRemindersRef.current.has(res.data.reminder._id?.toString()) || hasCurrentUserHandled(res.data.reminder)) {
                            console.log('[GlobalReminderModal] Reminder already handled. Skipping popup.');
                            return;
                        }
                        setReminder(res.data.reminder);
                        setVisible(true);
                        playSound();
                        return;
                    }
                } catch (err) {
                    console.error('[GlobalReminderModal] Fetch failed (403 or network). Falling back to basic payload.', err.message);
                }
            }

            // Fallback — if fetch fails, we MUST still show the popup, but with data we have
            console.warn('[GlobalReminderModal] Using fallback payload. RSVP will be disabled unless reminderId is present.');
            setReminder({
                _id: data.relatedData?.reminderId || data._id || null,
                title: data.title?.replace('Reminder: ', '') || 'Reminder',
                dateTime: new Date().toISOString(),
                notes: data.message || data.body || '',
                rsvpEnabled: !!data.relatedData?.reminderId, // Try to guess if we have an ID
                selectedUsers: []
            });
            setVisible(true);
            playSound();
        };

        window.addEventListener('reminder_received', handleReminderEvent);
        window.addEventListener('new_notification', handleReminderEvent);

        // ── Service Worker push message listener ──────────────────────────
        // When a push notification arrives, the SW posts a message to the page.
        // This triggers the modal even when the socket isn't connected.
        const handleSwMessage = async (event) => {
            if (event.data?.type !== 'PUSH_NOTIFICATION') return;
            const payload = event.data.payload;
            console.log('[GlobalReminderModal] SW push message received:', payload);

            // Reuse the same deduplication guard
            const eventId = payload.notificationId?.toString() || '';
            const now = Date.now();
            if (eventId && eventId === lastEventRef.current.id && now - lastEventRef.current.ts < 1000) {
                console.log('[GlobalReminderModal] SW push duplicate — skipped');
                return;
            }
            lastEventRef.current = { id: eventId, ts: now };

            // Try to fetch full reminder details if we have an ID
            const reminderId = payload.relatedData?.reminderId || payload.notificationId;
            console.log('[GlobalReminderModal] SW message mapped ID:', reminderId);

            if (reminderId) {
                console.log('[GlobalReminderModal] Fetching details from SW message for ID:', reminderId);
                try {
                    const res = await axios.get(`/events/${reminderId}`);
                    if (res.data?.reminder) {
                        if (handledRemindersRef.current.has(res.data.reminder._id?.toString()) || hasCurrentUserHandled(res.data.reminder)) {
                            console.log('[GlobalReminderModal] SW reminder already handled. Skipping popup.');
                            return;
                        }
                        setReminder(res.data.reminder);
                        setVisible(true);
                        playSound();
                        return;
                    }
                } catch (err) {
                    console.error('[GlobalReminderModal] SW fetch failed:', err.message);
                }
            }

            // SW Fallback
            console.warn('[GlobalReminderModal] SW Falling back to basic payload.');
            setReminder({
                _id: reminderId || null,
                title: payload.title?.replace('Reminder: ', '') || 'Reminder',
                dateTime: new Date().toISOString(),
                notes: payload.body || '',
                rsvpEnabled: !!reminderId, // Try to show buttons if we have an ID
            });
            console.log('[GlobalReminderModal] Opening modal from SW message');
            setVisible(true);
            playSound();
        };

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleSwMessage);
        }

        const handleHandledEvent = (event) => {
            handleReminderHandled(event.detail || {});
        };
        window.addEventListener('reminder_handled', handleHandledEvent);

        if ('BroadcastChannel' in window) {
            broadcastChannelRef.current = new BroadcastChannel(REMINDER_SYNC_CHANNEL);
            broadcastChannelRef.current.onmessage = (event) => {
                handleReminderHandled(event.data || {});
            };
        }

        const handleStorage = (event) => {
            if (event.key !== REMINDER_SYNC_STORAGE_KEY || !event.newValue) return;
            try {
                handleReminderHandled(JSON.parse(event.newValue));
            } catch (_) { }
        };
        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener('reminder_received', handleReminderEvent);
            window.removeEventListener('new_notification', handleReminderEvent);
            window.removeEventListener('reminder_handled', handleHandledEvent);
            window.removeEventListener('storage', handleStorage);
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.close();
                broadcastChannelRef.current = null;
            }
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', handleSwMessage);
            }
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('keydown', unlockAudio);
            stopSound();
            if (snoozeTimerRef.current) clearTimeout(snoozeTimerRef.current);
        };
    }, []);

    const playSound = () => {
        if (audioRef.current) { audioRef.current.play().catch(() => { }); setIsPlaying(true); }
    };
    const stopSound = () => {
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); }
    };
    const handleSnooze = async (minutes) => {
        if (!reminder?._id) { closeModal(); return; }
        setSnoozing(true);
        setActiveSnooze(minutes);

        const capturedReminder = { ...reminder };
        const reminderId = reminder._id.toString();

        try {
            await snoozeEvent(reminder._id, minutes);
            closeModal();
            publishReminderHandled({
                reminderId,
                userId: currentAdminIdRef.current,
                action: 'snoozed',
                duration: minutes,
            });
            snoozeTimerRef.current = setTimeout(() => {
                setReminder(capturedReminder);
                setVisible(true);
                playSound();
            }, minutes * 60 * 1000);
        } catch (err) {
            console.error('[GlobalReminderModal] snooze error:', err);
            closeModal();
        } finally {
            setSnoozing(false);
            setActiveSnooze(null);
        }
    };

    const handleRespond = async (rsvpValue) => {
        if (!reminder?._id) { closeModal(); return; }
        const reminderId = reminder._id.toString();
        setActiveRespond(rsvpValue);
        try {
            await acknowledgeEvent(reminder._id, rsvpValue);
            publishReminderHandled({
                reminderId,
                userId: currentAdminIdRef.current,
                action: rsvpValue === 'dismissed' ? 'dismissed' : 'acknowledged',
                rsvp: rsvpValue,
            });
        } catch (_) { }
        setActiveRespond(null);
        closeModal();
    };

    const getFilePreview = (attachment) => {
        if (!attachment) return null;
        const fileUrl = `${process.env.REACT_APP_BASE_URL}uploads/${attachment}`;
        const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(attachment);
        return (
            <div className="mt-3 flex items-center justify-center gap-2">
                {isImage ? (
                    <>
                        <img src={fileUrl} alt="Attachment" className="w-20 h-20 object-cover rounded" />
                        <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(fileUrl, '_blank')}>View</Button>
                    </>
                ) : (
                    <>
                        <FileOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                        <span className="text-sm">{attachment}</span>
                        <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(fileUrl, '_blank')}>Open</Button>
                    </>
                )}
            </div>
        );
    };

    const isMissed = dayjs(reminder?.dateTime).isBefore(dayjs().subtract(1, 'minute'));

    // Priority color mapping
    const PRIORITY_COLOR = {
        high: '#ff4d4f',
        medium: '#faad14',
        low: '#52c41a',
    };
    const priorityColor = PRIORITY_COLOR[reminder?.priority] || '#ff4d4f';

    return (
        <Modal
            open={visible}
            onCancel={undefined}
            closable={false}
            maskClosable={false}
            keyboard={false}
            centered
            width={480}
            title={
                <div className="flex items-center gap-2">
                    <BellOutlined style={{ color: priorityColor, fontSize: 24 }} className="animate-bounce" />
                    <div>
                        <p style={{ color: priorityColor, fontWeight: '900', margin: 0, fontSize: '18px', letterSpacing: '0.5px' }}>
                            {isMissed ? 'MISSED REMINDER' : 'ACTIVE REMINDER'}
                        </p>
                        {reminder?.eventType && (
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter m-0 -mt-1">
                                {reminder.eventType.replace('_', ' ')}
                            </p>
                        )}
                    </div>
                    {isRsvp && (
                        <span className="ml-auto text-[10px] font-black uppercase px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full border border-blue-200 shadow-sm">
                            RSVP Required
                        </span>
                    )}
                </div>
            }
            footer={
                <div className="flex flex-col gap-3 w-full">
                    {/* Snooze section — highlighted box */}
                    <div className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-2">
                            <ClockCircleOutlined className="text-slate-400" />
                            <span className="text-xs font-black uppercase tracking-wider text-slate-500">Snooze</span>
                        </div>
                        <div className="flex gap-1.5">
                            {SNOOZE_OPTIONS.map((opt) => (
                                <Button
                                    key={opt.value}
                                    size="small"
                                    loading={snoozing && activeSnooze === opt.value}
                                    disabled={snoozing && activeSnooze !== opt.value || activeRespond !== null}
                                    onClick={() => handleSnooze(opt.value)}
                                    className="text-[10px] font-bold border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-100 transition-all px-2.5"
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* RSVP buttons — each has its own spinner */}
                    {isCreator ? (
                        <div className="flex gap-2">
                             <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                loading={activeRespond === 'accepted'}
                                disabled={activeRespond !== null && activeRespond !== 'accepted'}
                                onClick={() => handleRespond('accepted')}
                                style={{ flex: 1, background: '#03488A' }}
                            >
                                Acknowledge & Close
                            </Button>
                        </div>
                    ) : isRsvp ? (
                        <div className="flex gap-2">
                            {RSVP_OPTIONS.map((opt) => (
                                <Button
                                    key={opt.value}
                                    loading={activeRespond === opt.value}
                                    disabled={activeRespond !== null && activeRespond !== opt.value}
                                    onClick={() => handleRespond(opt.value)}
                                    danger={opt.danger}
                                    style={{ flex: 1, ...(opt.style || {}) }}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                loading={activeRespond === 'accepted'}
                                disabled={activeRespond !== null && activeRespond !== 'accepted'}
                                onClick={() => handleRespond('accepted')}
                                style={{ flex: 1, background: '#03488A' }}
                            >
                                Acknowledge
                            </Button>
                            <Button
                                danger
                                icon={<CloseOutlined />}
                                loading={activeRespond === 'dismissed'}
                                disabled={activeRespond !== null && activeRespond !== 'dismissed'}
                                onClick={() => handleRespond('dismissed')}
                                style={{ flex: 1 }}
                            >
                                Dismiss
                            </Button>
                        </div>
                    )}
                </div>
            }
            bodyStyle={{
                padding: '20px 24px',
                background: isDarkMode
                    ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                    : 'linear-gradient(135deg, #fff7f7 0%, #ffffff 100%)',
            }}
        >
            {reminder && (
                <div className="text-center">
                    <p className="text-2xl font-black text-[#03488A] dark:text-blue-400 mb-1 leading-tight tracking-tight">
                        {reminder.title}
                    </p>
                    <p className="text-xs font-bold text-slate-400 mb-4 uppercase tracking-widest flex items-center justify-center gap-1.5">
                        <ClockCircleOutlined />
                        {dayjs(reminder.dateTime).format('MMM DD, YYYY · hh:mm A')}
                    </p>

                    <div className="flex flex-col gap-3">
                        {reminder.notes && (
                            <div className="text-sm text-slate-600 dark:text-slate-300 text-left bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-100 dark:border-slate-800 shadow-inner">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Message / Notes</p>
                                <p className="leading-relaxed">{reminder.notes}</p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            {reminder.user?.name && (
                                <div className="text-left bg-slate-50 dark:bg-slate-800/30 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Organizer</p>
                                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{reminder.user.name}</p>
                                </div>
                            )}
                            {reminder.selectedUsers?.length > 0 && (
                                <div className="text-left bg-slate-50 dark:bg-slate-800/30 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Audience / Participants</p>
                                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 leading-tight">
                                        {reminder.selectedUsers.map((u) => u.name || 'User').join(', ')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {reminder.meetLink && (
                            <Button 
                                type="primary" 
                                href={reminder.meetLink} 
                                target="_blank" 
                                icon={<VideoCameraOutlined />}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 border-none h-10 font-bold rounded-lg"
                            >
                                Join Online Meeting
                            </Button>
                        )}

                        {reminder.attachment && getFilePreview(reminder.attachment)}
                    </div>
                </div>
            )}
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
        </Modal>
    );
};

export default GlobalReminderModal;
